import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import PhotoAvatar from '../shared/Avatar';

const SEVERITY_CONFIG = {
  high:   { bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-700',   badge: 'bg-red-100 text-red-700',   dot: 'bg-red-500',   label: 'High' },
  medium: { bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400', label: 'Medium' },
  low:    { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',  badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400',  label: 'Low' },
};

const TYPE_ICON = {
  leavers:  '🚪',
  disabled: '🔒',
  ghost:    '👻',
  oversized: '📦',
};

function Avatar({ name, size = 7, personId }) {
  const initials = name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
  return (
    <PhotoAvatar personId={personId} className={`w-${size} h-${size} rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-600 flex-shrink-0`}>
      {initials}
    </PhotoAvatar>
  );
}

function StatCard({ label, value, sub, color = 'text-gray-800' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] font-semibold text-gray-600 mt-0.5">{label}</div>
      {sub && <div className="text-[9px] text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function RecommendationCard({ rec, expanded, onToggle }) {
  const cfg = SEVERITY_CONFIG[rec.severity];
  return (
    <div className={`border rounded-lg ${cfg.border} ${cfg.bg} overflow-hidden`}>
      <button
        className="w-full text-left p-3 flex items-start gap-3"
        onClick={onToggle}
      >
        <span className="text-lg leading-none mt-0.5">{TYPE_ICON[rec.type] || '⚠'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
            <span className={`text-[11px] font-semibold ${cfg.text}`}>{rec.title}</span>
          </div>
          <p className="text-[9px] text-gray-600 mt-1">{rec.detail}</p>
        </div>
        {rec.users.length > 0 && (
          <span className="text-[9px] text-gray-400 flex-shrink-0 mt-0.5">
            {expanded ? '▲ hide' : `▼ ${rec.users.length} user${rec.users.length > 1 ? 's' : ''}`}
          </span>
        )}
      </button>
      {expanded && rec.users.length > 0 && (
        <div className="border-t border-gray-200 bg-white px-3 py-2 space-y-1.5">
          {rec.users.map(u => (
            <div key={u.id} className="flex items-center gap-2">
              <Avatar name={u.displayName} size={6} personId={u.person?.id} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-800 truncate">{u.displayName}</div>
                <div className="text-[9px] text-gray-400 truncate">{u.userPrincipalName}</div>
              </div>
              {u.person && (
                <div className="text-right flex-shrink-0">
                  <div className="text-[9px] text-gray-500">{u.person.designation || '—'}</div>
                  {u.person.leave_date && (
                    <div className="text-[9px] text-red-600 font-medium">Left {formatDate(u.person.leave_date)}</div>
                  )}
                </div>
              )}
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${u.accountEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {u.accountEnabled ? 'Active' : 'Disabled'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SkuTable({ sku, expanded, onToggle }) {
  const utilPct = sku.total > 0 ? Math.round(sku.consumed / sku.total * 100) : 0;
  const barColor = utilPct > 90 ? 'bg-red-400' : utilPct > 70 ? 'bg-amber-400' : 'bg-green-400';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button className="w-full text-left p-3 flex items-center gap-3 hover:bg-gray-50" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-800">{sku.displayName}</div>
          <div className="text-[9px] text-gray-400 font-mono">{sku.skuPartNumber}</div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <div className="text-[10px] font-bold text-gray-800">{sku.consumed} <span className="font-normal text-gray-400">/ {sku.total}</span></div>
            <div className="text-[8px] text-gray-400">{sku.available} available</div>
          </div>
          <div className="w-20">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${utilPct}%` }} />
            </div>
            <div className="text-[8px] text-gray-400 text-right mt-0.5">{utilPct}%</div>
          </div>
          <span className="text-[9px] text-gray-400">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {sku.assignedUsers.length === 0 ? (
            <div className="px-3 py-3 text-[9px] text-gray-400 italic">No users assigned to this license.</div>
          ) : (
            sku.assignedUsers.map(u => (
              <div key={u.id} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50">
                <Avatar name={u.displayName} size={6} personId={u.person?.id} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold text-gray-800">{u.displayName}</div>
                  <div className="text-[9px] text-gray-400 truncate">{u.userPrincipalName}</div>
                </div>
                {u.person && (
                  <div className="text-right flex-shrink-0 text-[9px] text-gray-500">
                    <div>{u.person.designation || '—'}</div>
                    {u.person.leave_date && (
                      <div className="text-red-600 font-medium">Left {formatDate(u.person.leave_date)}</div>
                    )}
                  </div>
                )}
                <div className="flex gap-1 flex-shrink-0">
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${u.accountEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {u.accountEnabled ? 'Active' : 'Disabled'}
                  </span>
                  {!u.person && (
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">No record</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function M365LicensesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRecs, setExpandedRecs] = useState({});
  const [expandedSkus, setExpandedSkus] = useState({});
  const [activeTab, setActiveTab] = useState('recommendations');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await api.get('m365');
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to load license data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRec = (i) => setExpandedRecs(p => ({ ...p, [i]: !p[i] }));
  const toggleSku = (id) => setExpandedSkus(p => ({ ...p, [id]: !p[id] }));

  const highCount   = data?.recommendations?.filter(r => r.severity === 'high').length || 0;
  const mediumCount = data?.recommendations?.filter(r => r.severity === 'medium').length || 0;
  const totalSeats  = data?.skus?.reduce((s, k) => s + k.total, 0) || 0;
  const usedSeats   = data?.skus?.reduce((s, k) => s + k.consumed, 0) || 0;
  const freeSeats   = totalSeats - usedSeats;
  const leaver_waste = data?.recommendations?.filter(r => r.type === 'leavers').reduce((s, r) => s + r.saving, 0) || 0;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-gray-900">M365 License Management</h1>
          {data?.asOf && (
            <p className="text-[9px] text-gray-400 mt-0.5">
              Live data from Microsoft Entra ID — as of {new Date(data.asOf).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 disabled:opacity-50"
        >
          {loading ? '↻ Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-[10px] text-red-700">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-[10px] text-gray-400 text-center py-12">Loading license data from Microsoft Graph…</div>
      )}

      {data && (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total Licensed Seats" value={totalSeats} sub={`${data.skus.length} subscription${data.skus.length !== 1 ? 's' : ''}`} />
            <StatCard label="Seats In Use" value={usedSeats} sub={`${totalSeats > 0 ? Math.round(usedSeats / totalSeats * 100) : 0}% utilisation`} color="text-blue-700" />
            <StatCard label="Unused Seats" value={freeSeats} sub="potential to reduce" color={freeSeats > 3 ? 'text-amber-600' : 'text-gray-800'} />
            <StatCard
              label="Action Items"
              value={highCount + mediumCount}
              sub={highCount > 0 ? `${highCount} high priority` : 'No high-priority issues'}
              color={highCount > 0 ? 'text-red-600' : mediumCount > 0 ? 'text-amber-600' : 'text-green-600'}
            />
          </div>

          {leaver_waste > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
              <span className="text-xl">🚪</span>
              <div>
                <div className="text-[11px] font-bold text-red-700">
                  {leaver_waste} license seat{leaver_waste > 1 ? 's' : ''} held by people who have already left
                </div>
                <div className="text-[9px] text-red-600 mt-0.5">
                  Removing these licenses immediately frees up paid seats and reduces security risk.
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            {[
              { id: 'recommendations', label: `Recommendations${data.recommendations.length > 0 ? ` (${data.recommendations.length})` : ''}` },
              { id: 'subscriptions', label: `Subscriptions (${data.skus.length})` },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-2 text-[10px] font-semibold border-b-2 -mb-px ${activeTab === t.id ? 'border-gold text-gold' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Recommendations tab */}
          {activeTab === 'recommendations' && (
            <div className="space-y-2">
              {data.recommendations.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-[11px] font-semibold text-gray-700">No recommendations</div>
                  <div className="text-[9px] text-gray-400 mt-1">Your license assignments look clean and well-optimised.</div>
                </div>
              ) : (
                <>
                  {['high', 'medium', 'low'].map(sev => {
                    const recs = data.recommendations.filter(r => r.severity === sev);
                    if (recs.length === 0) return null;
                    const cfg = SEVERITY_CONFIG[sev];
                    return (
                      <div key={sev}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                          <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">{cfg.label} Priority</span>
                        </div>
                        <div className="space-y-2">
                          {recs.map((rec, i) => (
                            <RecommendationCard
                              key={i}
                              rec={rec}
                              expanded={!!expandedRecs[`${sev}-${i}`]}
                              onToggle={() => toggleRec(`${sev}-${i}`)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-[9px] text-gray-400 pt-2 border-t border-gray-100">
                    Recommendations are generated automatically by comparing Entra ID license data with the people records in this platform.
                    Cost savings depend on your per-seat pricing — check your Microsoft billing portal for exact figures.
                  </div>
                </>
              )}
            </div>
          )}

          {/* Subscriptions tab */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-2">
              {data.skus.length === 0 ? (
                <div className="text-[10px] text-gray-400 text-center py-8">No paid subscriptions found.</div>
              ) : (
                data.skus.map(sku => (
                  <SkuTable
                    key={sku.skuId}
                    sku={sku}
                    expanded={!!expandedSkus[sku.skuId]}
                    onToggle={() => toggleSku(sku.skuId)}
                  />
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
